import React, { useState, useEffect } from 'react';
import { sets, tags } from '../utils/api';
import { toast } from 'react-toastify';
import CreateSetModal from './CreateSetModal';
import EditSetModal from './EditSetModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const SetManager = ({ onUpdate, navigate }) => {
  const [userSets, setUserSets] = useState([]);
  const [userTags, setUserTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [deletingSet, setDeletingSet] = useState(null);

  useEffect(() => {
    fetchData();

    // Add event listener for set changes
    const handleSetChange = () => {
      if (onUpdate) onUpdate();
    };

    window.addEventListener('setChange', handleSetChange);
    return () => window.removeEventListener('setChange', handleSetChange);
  }, [onUpdate]);

  const fetchData = async () => {
    try {
      const [fetchedSets, fetchedTags] = await Promise.all([
        sets.getAll(),
        tags.getAll()
      ]);
      console.log('Fetched sets:', fetchedSets);
      setUserSets(fetchedSets);
      setUserTags(fetchedTags);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleDeleteSet = async (setId) => {
    try {
      await sets.delete(setId);
      if (onUpdate) onUpdate();
      toast.success('Set deleted successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to delete set');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 p-4">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Create Set
        </button>
      </div>

      {/* Sets list */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {userSets.map(set => (
            <li key={set.id} className="px-4 py-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{set.name}</h3>
                  {set.description && (
                    <p className="text-sm text-gray-500 mt-1">{set.description}</p>
                  )}
                  <div className="mt-2 flex items-center space-x-2">
                    <span className="text-xs text-gray-500">
                      {set.coin_count || 0} {(set.coin_count || 0) === 1 ? 'coin' : 'coins'}
                    </span>
                    <span className="text-xs text-gray-300">•</span>
                    <span className="text-xs text-gray-500">
                      {set.field_preferences?.visible_fields?.length || 0} fields
                    </span>
                    {set.type === 'dynamic' && (
                      <>
                        <span className="text-xs text-gray-300">•</span>
                        <span className="text-xs text-gray-500">
                          {((set.filter_criteria?.all_tags?.length || 0) + (set.filter_criteria?.any_tags?.length || 0))} tags
                        </span>
                        {(set.filter_criteria?.column_filters || set.filter_criteria?.filters) && (
                          <>
                            <span className="text-xs text-gray-300">•</span>
                            <span className="text-xs text-gray-500">
                              {(
                                (set.filter_criteria?.column_filters ? Object.values(set.filter_criteria.column_filters).filter(v => v).length : 0) +
                                (set.filter_criteria?.filters?.length || 0)
                              )} filters
                            </span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="flex items-center">
                  <button
                    onClick={() => setEditingSet(set)}
                    className="ml-2 text-indigo-600 hover:text-indigo-900"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => setDeletingSet(set)}
                    className="ml-2 text-red-600 hover:text-red-900"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </li>
          ))}
          {userSets.length === 0 && (
            <li className="px-4 py-4 sm:px-6 text-gray-500 text-sm">
              No sets created yet. Click "Create Set" above to create your first set.
            </li>
          )}
        </ul>
      </div>

      {/* Create Set Modal */}
      {showCreateModal && (
        <CreateSetModal
          onClose={() => setShowCreateModal(false)}
          onUpdate={() => {
            fetchData();
            if (onUpdate) onUpdate();
          }}
          userTags={userTags}
          navigate={navigate}
        />
      )}

      {/* Edit Set Modal */}
      {editingSet && (
        <EditSetModal
          set={editingSet}
          onClose={() => setEditingSet(null)}
          onUpdate={() => {
            fetchData();
            if (onUpdate) onUpdate();
          }}
          sortPreferences={{
            grid: { field: 'year', direction: 'asc' },
            carousel: { field: 'year', direction: 'asc' },
            timeline: { field: 'year', direction: 'asc' }
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!deletingSet}
        title="Delete Set"
        message={`Are you sure you want to delete "${deletingSet?.name}"? This action cannot be undone.`}
        onConfirm={() => {
          handleDeleteSet(deletingSet.id);
          setDeletingSet(null);
        }}
        onCancel={() => setDeletingSet(null)}
      />
    </div>
  );
};

export default SetManager;
